import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { pageSEO } from '../utils/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ExtendReservationModal from '../components/ExtendReservationModal';
import { useTranslation } from 'react-i18next';
import {
  localizeCarClass,
  localizeLocation,
  localizeOption,
  localizeVehicleModel,
} from '../utils/localization';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

interface PaymentBreakdown {
  rental_fee: number;
  insurance_fee: number;
  delivery_fee: number;
  coupon_discount?: number;
  [key: string]: number | undefined;
}

interface Payment {
  id?: number;
  reservation_id: number;
  payment_key: string;
  order_id: string;
  amount: number;
  breakdown?: PaymentBreakdown;
  payment_type: 'initial' | 'extension' | 'additional';
  status: 'pending' | 'approved' | 'completed' | 'failed' | 'cancelled';
  payment_method?: string;
  approved_at?: string;
  receipt_url?: string;
  created_at?: string;
}

interface Review {
  id: number;
  rating: number;
  content: string;
  created_at: string;
}

interface Reservation {
  id: number;
  reservation_number: string;
  user_name: string;
  user_phone: string;
  user_email: string;
  user_birth_date?: string;
  vehicle_name: string;
  vehicle_class?: string;
  vehicle_number?: string;
  start_date: string;
  end_date: string;
  pickup_location?: string;
  return_location?: string;
  has_own_insurance?: string;
  special_request?: string;
  total_amount: number;
  total_paid_amount?: number;
  payment_status: string;
  payment_method?: string;
  payment_approved_at?: string;
  payment_breakdown?: PaymentBreakdown;
  order_id?: string;
  status: string;
  payments?: Payment[];
  has_review?: boolean;
  review?: Review | null;
}

const ReservationDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language.startsWith('en');
  const { reservationNumber } = useParams<{ reservationNumber: string }>();
  const [searchParams] = useSearchParams();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [reviewToken, setReviewToken] = useState<string>('');
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const seo = pageSEO.reservationDetail;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchReservations = async () => {
      setIsLoading(true);
      setError('');

      try {
        // Query string에서 토큰 확인
        const token = searchParams.get('token');
        const reservationNumberParam = searchParams.get('reservation_number');
        const name = searchParams.get('name');
        const phone = searchParams.get('phone');

        let response;

        if (token) {
          // 토큰으로 조회 (base64 인코딩된 예약번호:이름)
          response = await axios.get(`${API_BASE_URL}/reservations/search-by-token`, {
            params: { token }
          });
        } else if (reservationNumberParam && name) {
          // 예약번호 + 이름으로 조회 (기존 방식 호환)
          response = await axios.get(`${API_BASE_URL}/reservations/search-local`, {
            params: {
              reservation_number: reservationNumberParam,
              customer_name: name
            }
          });
        } else if (name && phone) {
          // 이름 + 전화번호로 조회
          response = await axios.get(`${API_BASE_URL}/reservations/search-local`, {
            params: {
              customer_name: name,
              customer_phone: phone
            }
          });
        } else if (reservationNumber) {
          // 이전 방식 호환성: URL path로 예약번호만 있는 경우 (더 이상 지원 안 함)
          setError(t('reservationDetail.securityError'));
          setIsLoading(false);
          return;
        } else {
          setError(t('reservationDetail.invalidSearch'));
          setIsLoading(false);
          return;
        }

        setReservations(response.data);
        console.log('Reservation data:', response.data);

        // 토큰 생성 (리뷰 작성용)
        const currentToken = searchParams.get('token');
        if (currentToken) {
          // 이미 토큰이 있으면 그대로 사용
          setReviewToken(currentToken);
        } else if (response.data && response.data.length > 0) {
          // 토큰이 없으면 예약번호:이름으로 새로 생성 (UTF-8 지원)
          const firstReservation = response.data[0];
          const tokenData = `${firstReservation.reservation_number}:${firstReservation.user_name}`;
          const newToken = btoa(unescape(encodeURIComponent(tokenData)));
          setReviewToken(newToken);
        }
      } catch (err: any) {
        console.error('예약 조회 실패:', err);
        if (err.response?.status === 404) {
          setError(t('reservationDetail.notFoundByName'));
        } else if (err.response?.status === 400) {
          setError(t('reservationDetail.invalidInfo'));
        } else {
          setError(t('reservationDetail.searchError'));
        }
        setReservations([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReservations();
  }, [reservationNumber, searchParams, t]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(isEnglish ? 'en-US' : 'ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(isEnglish ? 'en-US' : 'ko-KR').format(price);
  };

  const formatMoney = (price: number) => `${formatPrice(price)}${t('common.won')}`;

  const displayText = (value?: string) => {
    if (!value) return '';
    if (!isEnglish) return value;
    if (value === '없음') return 'None';
    if (value === '미가입') return 'Not enrolled';
    if (value === '가입') return 'Enrolled';
    return localizeOption(value, i18n.language);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { text: string; color: string } } = {
      pending: { text: t('reservationDetail.status.pending'), color: 'bg-yellow-100 text-yellow-800' },
      confirmed: { text: t('reservationDetail.status.confirmed'), color: 'bg-green-100 text-green-800' },
      in_use: { text: t('reservationDetail.status.inUse'), color: 'bg-blue-100 text-blue-800' },
      completed: { text: t('reservationDetail.status.completed'), color: 'bg-gray-100 text-gray-800' },
      cancelled: { text: t('reservationDetail.status.cancelled'), color: 'bg-red-100 text-red-800' }
    };

    const statusInfo = statusMap[status] || { text: status, color: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
        {statusInfo.text}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { text: string; color: string } } = {
      pending: { text: t('reservationDetail.paymentStatus.pending'), color: 'bg-yellow-100 text-yellow-800' },
      completed: { text: t('reservationDetail.paymentStatus.completed'), color: 'text-green-600' },
      approved: { text: t('reservationDetail.paymentStatus.completed'), color: 'text-green-600' },
      failed: { text: t('reservationDetail.paymentStatus.failed'), color: 'bg-red-100 text-red-800' },
      cancelled: { text: t('reservationDetail.paymentStatus.cancelled'), color: 'bg-gray-100 text-gray-800' }
    };

    const statusInfo = statusMap[status] || { text: status, color: 'bg-gray-100 text-gray-800' };

    // 결제완료/승인인 경우 텍스트만 표시, 나머지는 배지 스타일
    if (status === 'completed' || status === 'approved') {
      return (
        <span className={`text-sm font-bold ${statusInfo.color}`}>
          {statusInfo.text}
        </span>
      );
    }

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
        {statusInfo.text}
      </span>
    );
  };

  // 환불 정책 계산 함수
  const calculateRefund = (startDate: string, totalAmount: number) => {
    const now = new Date();
    const rentalDate = new Date(startDate);
    const diffTime = rentalDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let refundRate = 0;
    let refundPolicy = '';

    if (diffDays >= 7) {
      refundRate = 1.0; // 100%
      refundPolicy = t('reservationDetail.refundPolicy.sevenDays');
    } else if (diffDays >= 3) {
      refundRate = 0.7; // 70%
      refundPolicy = t('reservationDetail.refundPolicy.threeDays');
    } else if (diffDays >= 1) {
      refundRate = 0.5; // 50%
      refundPolicy = t('reservationDetail.refundPolicy.oneDay');
    } else {
      refundRate = 0; // 0%
      refundPolicy = t('reservationDetail.refundPolicy.within24');
    }

    const refundAmount = Math.floor(totalAmount * refundRate);

    return {
      refundRate,
      refundAmount,
      refundPolicy,
      daysUntilRental: diffDays,
      canCancel: diffDays >= 1 // 24시간 이내는 취소 불가
    };
  };

  // 예약 취소 처리 함수
  const handleCancelReservation = async (reservation: Reservation) => {
    // 실제 결제된 총 금액 (초기 + 연장) 기준으로 환불 금액 계산
    const totalPaidAmount = reservation.total_paid_amount || reservation.total_amount;
    const refundInfo = calculateRefund(reservation.start_date, totalPaidAmount);

    if (!refundInfo.canCancel) {
      alert(t('reservationDetail.cancelUnavailable'));
      return;
    }

    const confirmMessage =
      `${t('reservationDetail.cancelConfirm')}\n\n` +
      `${refundInfo.refundPolicy}\n` +
      `${t('reservationDetail.expectedRefund')}: ${formatMoney(refundInfo.refundAmount)}\n\n` +
      `(${t('reservationDetail.totalPaidAmount')}: ${formatMoney(totalPaidAmount)})`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // 예약 취소 API 호출
      const response = await axios.post(
        `${API_BASE_URL}/payments/cancel`,
        {
          reservation_id: reservation.id,
          cancel_amount: refundInfo.refundAmount,
          cancel_reason: `${t('reservationDetail.cancelReason')} (${refundInfo.refundPolicy})`
        }
      );

      if (response.data.success) {
        alert(`${t('reservationDetail.cancelledAlert')}\n${t('reservationDetail.refundAmount')}: ${formatMoney(refundInfo.refundAmount)}`);
        // 페이지 새로고침하여 최신 상태 반영
        window.location.reload();
      } else {
        alert(t('reservationDetail.cancelFailed'));
      }
    } catch (err: any) {
      console.error('예약 취소 실패:', err);
      alert(`${t('reservationDetail.cancelError')}\n${err.response?.data?.detail || t('reservationDetail.contactAdmin')}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://goodtimerent.kr/reservation/detail" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <Header />
      <div className="min-h-screen bg-gray-50">
        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/my-reservations')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">{t('reservationDetail.backToList')}</span>
          </button>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('reservationDetail.title')}</h1>
            <p className="text-gray-600">{t('reservationDetail.subtitle')}</p>
          </div>

          {isLoading ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">{t('reservationDetail.loading')}</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-red-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-red-600 text-lg font-medium">{error}</p>
              <button
                onClick={() => navigate('/reservation')}
                className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('reservationDetail.searchAgain')}
              </button>
            </div>
          ) : reservations.length > 0 ? (
            <div className="space-y-4">
              {reservations.map((reservation) => (
                <div
                  key={reservation.id}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold mb-1">
                        {reservation.vehicle_name ? localizeVehicleModel(reservation.vehicle_name, i18n.language) : t('reservationDetail.noVehicleInfo')}
                      </h3>
                      {reservation.vehicle_class && (
                        <p className="text-sm text-gray-600">{localizeCarClass(reservation.vehicle_class, i18n.language)}</p>
                      )}
                      {reservation.vehicle_number && (
                        <p className="text-sm text-gray-500">{t('reservationDetail.vehicleNumber')}: {reservation.vehicle_number}</p>
                      )}
                      <p className="text-sm text-gray-600">
                        {t('reservationDetail.reservationNumber')}: {reservation.reservation_number}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      {getStatusBadge(reservation.status)}
                    </div>
                  </div>

                  <div className="border-t pt-4 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{t('reservationDetail.customerInfo')}</p>
                        <p className="font-medium">{reservation.user_name}</p>
                        <p className="text-sm text-gray-600">{reservation.user_phone}</p>
                        {reservation.user_email && (
                          <p className="text-sm text-gray-600">{reservation.user_email}</p>
                        )}
                        {reservation.user_birth_date && (
                          <p className="text-sm text-gray-600">{t('reservationDetail.birthDate')}: {reservation.user_birth_date}</p>
                        )}
                        {reservation.has_own_insurance && (
                          <p className="text-sm text-gray-600">
                            {t('reservationDetail.insurance')}: {reservation.has_own_insurance === 'Y' ? t('reservationDetail.enrolled') : t('reservationDetail.notEnrolled')}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{t('reservationDetail.rentalPeriod')}</p>
                        <p className="text-sm">
                          <span className="font-medium">{t('reservationDetail.pickup')}:</span> {formatDate(reservation.start_date)}
                        </p>
                        <p className="text-sm">
                          <span className="font-medium">{t('reservationDetail.return')}:</span> {formatDate(reservation.end_date)}
                        </p>
                      </div>
                    </div>

                    {/* 대여/반납 위치 */}
                    {(reservation.pickup_location || reservation.return_location) && (
                      <div className="grid md:grid-cols-2 gap-4">
                        {reservation.pickup_location && (
                          <div>
                            <p className="text-sm text-gray-600 mb-1">{t('reservationDetail.pickupLocation')}</p>
                            <p className="text-sm font-medium text-gray-900">{localizeLocation(reservation.pickup_location, i18n.language)}</p>
                          </div>
                        )}
                        {reservation.return_location && (
                          <div>
                            <p className="text-sm text-gray-600 mb-1">{t('reservationDetail.returnLocation')}</p>
                            <p className="text-sm font-medium text-gray-900">{localizeLocation(reservation.return_location, i18n.language)}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 특별 요청사항 */}
                    {reservation.special_request && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{t('reservationDetail.specialRequest')}</p>
                        <p className="text-sm text-gray-900">{displayText(reservation.special_request)}</p>
                      </div>
                    )}

                    {/* 결제 정보 */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900">{t('reservationDetail.paymentInfo')}</h4>
                        {reservation.payment_status && getPaymentStatusBadge(reservation.payment_status)}
                      </div>

                      <div className="mb-4">
                        <p className="text-xs text-gray-500 mb-1">{t('reservationDetail.totalPayment')}</p>
                        <p className="text-lg font-bold text-blue-600">
                          {formatMoney(reservation.total_paid_amount || reservation.total_amount)}
                        </p>
                      </div>

                      {/* 모든 결제 내역 표시 */}
                      {reservation.payments && reservation.payments.length > 0 && (
                        <div className="space-y-3">
                          <p className="text-xs font-semibold text-gray-700">{t('reservationDetail.paymentHistory')}</p>
                          {reservation.payments.map((payment, index) => {
                            const hasExtensionPayment = reservation.payments?.some(p => p.payment_type === 'extension');
                            return (
                              <div key={payment.id || index} className="bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium text-gray-700">
                                    {payment.payment_type === 'extension' && t('reservationDetail.extensionPayment')}
                                    {payment.payment_type === 'additional' && t('reservationDetail.additionalPayment')}
                                  </span>
                                  {/* 연장결제가 있는 경우에만 결제완료 텍스트 표시 */}
                                  {hasExtensionPayment && (payment.status === 'completed' || payment.status === 'approved') ? (
                                    <span className="text-xs font-bold text-green-600">
                                      {t('reservationDetail.paymentStatus.completed')}
                                    </span>
                                  ) : !hasExtensionPayment && (payment.status === 'completed' || payment.status === 'approved') ? (
                                    <></>
                                  ) : (
                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                      {t(`reservationDetail.paymentStatusRaw.${payment.status}`, { defaultValue: payment.status })}
                                    </span>
                                  )}
                                </div>

                                <div className="space-y-1">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">{t('reservationDetail.paymentAmount')}</span>
                                    <span className="text-gray-900 font-semibold">{formatMoney(Number(payment.amount))}</span>
                                  </div>

                                  {payment.payment_method && (
                                    <div className="flex justify-between text-xs">
                                      <span className="text-gray-500">{t('reservationDetail.paymentMethod')}</span>
                                      <span className="text-gray-700">{payment.payment_method}</span>
                                    </div>
                                  )}

                                  {payment.approved_at && (
                                    <div className="flex justify-between text-xs">
                                      <span className="text-gray-500">{t('reservationDetail.approvedAt')}</span>
                                      <span className="text-gray-700">{formatDate(payment.approved_at)}</span>
                                    </div>
                                  )}

                                  {payment.order_id && (
                                    <div className="flex justify-between text-xs">
                                      <span className="text-gray-500">{t('reservationDetail.orderId')}</span>
                                      <span className="text-gray-700 font-mono">{payment.order_id}</span>
                                    </div>
                                  )}

                                  {/* 결제 세부 항목 */}
                                  {payment.breakdown && (
                                    <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                                      {payment.breakdown.rental_fee !== undefined && payment.breakdown.rental_fee > 0 && (
                                        <div className="flex justify-between text-xs">
                                          <span className="text-gray-500">{t('reservationDetail.rentalFee')}</span>
                                          <span className="text-gray-700">{formatMoney(payment.breakdown.rental_fee)}</span>
                                        </div>
                                      )}
                                      {payment.breakdown.insurance_fee !== undefined && payment.breakdown.insurance_fee > 0 && (
                                        <div className="flex justify-between text-xs">
                                          <span className="text-gray-500">{t('reservationDetail.insuranceFee')}</span>
                                          <span className="text-gray-700">{formatMoney(payment.breakdown.insurance_fee)}</span>
                                        </div>
                                      )}
                                      {payment.breakdown.delivery_fee !== undefined && payment.breakdown.delivery_fee > 0 && (
                                        <div className="flex justify-between text-xs">
                                          <span className="text-gray-500">{t('reservationDetail.deliveryFee')}</span>
                                          <span className="text-gray-700">{formatMoney(payment.breakdown.delivery_fee)}</span>
                                        </div>
                                      )}
                                      {payment.breakdown.coupon_discount !== undefined && payment.breakdown.coupon_discount > 0 && (
                                        <div className="flex justify-between text-xs">
                                          <span className="text-gray-500">{t('reservationDetail.couponDiscount')}</span>
                                          <span className="text-red-600">-{formatMoney(payment.breakdown.coupon_discount)}</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* 예약 관리 버튼 */}
                    <div className="border-t pt-4">
                      {reservation.status !== 'cancelled' && (
                        <div className="flex gap-2">
                          {(reservation.status === 'confirmed' || reservation.status === 'in_use') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedReservation(reservation);
                                setIsExtendModalOpen(true);
                              }}
                              className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                            >
                              {t('reservationDetail.extendReservation')}
                            </button>
                          )}
                          {/* 출고 전(pending, confirmed)에만 취소 가능 */}
                          {(reservation.status === 'pending' || reservation.status === 'confirmed') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelReservation(reservation);
                              }}
                              className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors"
                            >
                              {t('reservationDetail.cancelReservation')}
                            </button>
                          )}
                          {/* 이용 완료 시 리뷰 작성/보기 가능 */}
                          {reservation.status === 'completed' && (
                            <>
                              {(() => {
                                console.log('Reservation:', reservation.reservation_number, 'has_review:', reservation.has_review, 'review:', reservation.review);
                                return null;
                              })()}
                              {reservation.has_review ? (
                                <button
                                  onClick={(e) => {
                                    console.log('Review button clicked');
                                    e.stopPropagation();
                                    setSelectedReview(reservation.review || null);
                                    setIsReviewModalOpen(true);
                                  }}
                                  className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors"
                                >
                                  {t('reservationDetail.viewReview')}
                                </button>
                              ) : reviewToken ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/review/${encodeURIComponent(reviewToken)}`);
                                  }}
                                  className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                                >
                                  {t('reservationDetail.writeReview')}
                                </button>
                              ) : null}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-gray-600 text-lg">{t('reservationDetail.empty')}</p>
              <button
                onClick={() => navigate('/reservation')}
                className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('reservationDetail.searchAgain')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 예약 연장 모달 */}
      {selectedReservation && (
        <ExtendReservationModal
          isOpen={isExtendModalOpen}
          onClose={() => {
            setIsExtendModalOpen(false);
            setSelectedReservation(null);
          }}
          reservation={{
            id: selectedReservation.id,
            reservation_number: selectedReservation.reservation_number,
            vehicle_name: selectedReservation.vehicle_name,
            end_date: selectedReservation.end_date,
            return_location: selectedReservation.return_location,
          }}
          onSuccess={() => {
            // 예약 목록 새로고침
            window.location.reload();
          }}
        />
      )}

      {/* 리뷰 보기 모달 */}
      {isReviewModalOpen && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{t('reservationDetail.customerReview')}</h2>
                <button
                  onClick={() => {
                    setIsReviewModalOpen(false);
                    setSelectedReview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* 별점 표시 */}
              <div className="mb-6">
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg
                      key={star}
                      className={`w-8 h-8 ${
                        star <= selectedReview.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                      }`}
                      viewBox="0 0 24 24"
                    >
                      <path d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  ))}
                  <span className="ml-2 text-xl font-semibold text-gray-900">{selectedReview.rating}{t('reservationDetail.points')}</span>
                </div>
              </div>

              {/* 리뷰 내용 */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('reservationDetail.reviewContent')}</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedReview.content}</p>
                </div>
              </div>

              {/* 작성일 */}
              <div className="text-sm text-gray-500 mb-6">
                {t('reservationDetail.createdAt')}: {new Date(selectedReview.created_at).toLocaleString(isEnglish ? 'en-US' : 'ko-KR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>

              {/* 닫기 버튼 */}
              <button
                onClick={() => {
                  setIsReviewModalOpen(false);
                  setSelectedReview(null);
                }}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-4 rounded-lg font-medium transition-colors"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default ReservationDetailPage;
